import { Box, Typography, Paper, CircularProgress, IconButton } from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { Paperclip, Eye } from 'lucide-react';
import { useAttachments, type AttachmentFile } from '@lingaai/shared-hooks';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

interface AttachmentsSectionProps {
  nodeId: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentsSection({ nodeId }: AttachmentsSectionProps) {
  const { data: attachments = [], isLoading, error } = useAttachments(nodeId);

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error loading attachments: {error instanceof Error ? error.message : 'Unknown error'}</Typography>
      </Box>
    );
  }

  if (attachments.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Paperclip size={ICON_SIZES.xl} strokeWidth={ICON_STROKE} style={{ opacity: 0.3, marginBottom: 16 }} />
          <Typography variant="body1" color="text.secondary">
            No attachments
          </Typography>
        </Paper>
      </Box>
    );
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'File Name',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<AttachmentFile>) => (
        <span
          onClick={() => window.open(params.row.url, '_blank')}
          style={{
            color: 'inherit',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: 'size',
      headerName: 'Size',
      width: 120,
      valueFormatter: (value: number) => formatFileSize(value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AttachmentFile>) => (
        <IconButton size="small" onClick={() => window.open(params.row.url, '_blank')} sx={{ color: 'text.secondary' }} title="View">
          <Eye size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
        </IconButton>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <DataGrid
        rows={attachments}
        columns={columns}
        getRowId={(row) => row.name}
        autoHeight
        disableRowSelectionOnClick
        hideFooter={attachments.length <= 10}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[5, 10, 25]}
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'default',
          },
        }}
      />
    </Paper>
  );
}
